import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { ButtonDarkGrey } from "./button";

// ─── Panel container ────────────────────────────────────────────────────────

export const Panel = styled(Box)(({ theme }) => ({
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
}));

// ─── Data row (38px compact) ────────────────────────────────────────────────

export const PanelRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0.875, 1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
  minHeight: 38,
  "&:last-child": { borderBottom: "none" },
}));

// ─── Section divider bar ────────────────────────────────────────────────────

export const SectionBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0.5, 1.5),
  backgroundColor: theme.palette.background.secondary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(0.75),
  minHeight: 30,
}));

export const SectionBarLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.68rem",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
}));

// ─── Row label (fixed-width uppercase) ──────────────────────────────────────

export const RowLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  width: 110,
  flexShrink: 0,
}));

// ─── Row value ──────────────────────────────────────────────────────────────

export const RowValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.84rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  flex: 1,
  minWidth: 0,
}));

// ─── Monospace code span ────────────────────────────────────────────────────

export const CodeMono = styled("span")(({ theme }) => ({
  fontFamily: "'SF Mono', 'Cascadia Code', 'Fira Code', monospace",
  fontWeight: 700,
  fontSize: "0.9rem",
  letterSpacing: 2,
  color: theme.palette.primary.dark,
}));

// ─── Warning observation strip ──────────────────────────────────────────────

export const ObsStrip = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.75, 1.5),
  backgroundColor: alpha(theme.palette.warning.main, 0.06),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: `3px solid ${theme.palette.warning.main}`,
  "&:last-child": { borderBottom: "none" },
}));

// ─── Colored value badge ────────────────────────────────────────────────────

export const ValueBadge = styled(Box)(({ theme, colorKey }) => {
  const map = {
    success: {
      bg: alpha(theme.palette.success.main, 0.08),
      color: theme.palette.success.dark,
    },
    error: {
      bg: alpha(theme.palette.error.main, 0.08),
      color: theme.palette.error.dark,
    },
    primary: {
      bg: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.dark,
    },
    warning: {
      bg: alpha(theme.palette.warning.main, 0.08),
      color: theme.palette.warning.dark,
    },
    normal: {
      bg: alpha(theme.palette.grey[200], 0.6),
      color: theme.palette.text.primary,
    },
  };

  const v = map[colorKey] || map.normal;

  return {
    backgroundColor: v.bg,
    color: v.color,
    borderRadius: 4,
    padding: theme.spacing(0.375, 1),
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.82rem",
    flexShrink: 0,
    whiteSpace: "nowrap",
  };
});

// ─── Highlighted total row ──────────────────────────────────────────────────

export const TotalRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 1.5),
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: 44,
  "&:last-child": { borderBottom: "none" },
}));

// ─── Small action button for section bars ───────────────────────────────────

export const PanelActionBtn = styled(ButtonDarkGrey)(({ theme }) => ({
  fontSize: "0.72rem",
  fontWeight: 600,
  padding: theme.spacing(0.25, 1),
  minHeight: 24,
  borderRadius: 4,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  color: theme.palette.primary.dark,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
  },
}));

// ─── Copy button with feedback ──────────────────────────────────────────────

export function CopyBtn({ text, tooltip = "Copiar" }) {
  const theme = useTheme();
  const [ok, setOk] = useState(false);

  const handle = () => {
    navigator.clipboard.writeText(text);
    setOk(true);
    setTimeout(() => setOk(false), 1200);
  };

  return (
    <Tooltip title={ok ? "Copiado!" : tooltip}>
      <IconButton
        size="small"
        onClick={handle}
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1,
          color: ok ? theme.palette.success.main : theme.palette.text.disabled,
          "&:hover": {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {ok ? (
          <CheckIcon sx={{ fontSize: 14 }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        )}
      </IconButton>
    </Tooltip>
  );
}
